import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { WebChatThemeProps } from "@42maru/webchat";
import { INVALID_CODES } from "@/core/validation";
import { isValidTitle, isValidAlias, TITLE_MAX_LENGTH } from "@/util/helper/webchatThemeHelper";
import { useUploadedFiles } from "@/hook";
import { ImageUploadBtn, TextInputGroup } from "@/component/input";
import ClickableImg from "@/component/ClickableImg";
import { DeleteIcon } from "@/assets/svgs/icons";

import { WebchatThemeFormProps } from ".";


const WebchatBaseInputGroup: React.FC<WebchatThemeFormProps> = (props) => {
    const { theme, legend, onChange } = props;

    const intl = useIntl();
    const { add, remove } = useUploadedFiles();

    const titleValidation = useMemo(() => {
        const validation = isValidTitle(theme.title ?? "");
        let errorText = undefined;
        if (validation.code === INVALID_CODES.MAX) {
            errorText = intl.formatMessage({ id: "i09049" }, { maxLength: TITLE_MAX_LENGTH });
        }
        return {
            isValid: validation.isValid,
            errorText
        };
    }, [theme.title, intl]);

    const aliasValidation = useMemo(() => {
        const validation = isValidAlias(theme.alias);
        let errorText = undefined;
        if (validation.code === INVALID_CODES.INVALID_FORMAT) {
            errorText = intl.formatMessage({ id: "i09015" });
        }
        return { isValid: validation.isValid, errorText };
    }, [theme.alias, intl]);

    const onUploadFile = (key: keyof WebChatThemeProps, file: File, url: string) => {
        if (theme[key] && typeof theme[key] === "string") {
            remove(theme[key] as string);
        }
        add(file, url);
        onChange({ ...theme, [key]: url });
    };

    const onRemoveFile = (key: keyof WebChatThemeProps) => {
        const url = theme[key] as string;
        if (url) {
            remove(url);
        }
        onChange({ ...theme, [key]: undefined });
    };

    return (
        <fieldset className="label-input-grid-[180] gap-x-2 gap-y-4">
            <legend className="sr-only">
                {legend}
            </legend>
            <div className="col-span-2 flex flex-col gap-1.5">
                <label htmlFor="webchat-setting-title" className="field-label">
                    {intl.formatMessage({ id: "i09010" })}
                </label>
                <TextInputGroup required
                                id="webchat-setting-title"
                                value={theme.title}
                                invalid={!titleValidation.isValid}
                                invalidText={titleValidation.errorText}
                                onChange={value => onChange({ ...theme, title: value })}
                />
            </div>

            <div className="col-span-2 flex flex-col gap-1.5">
                <label htmlFor="webchat-setting-alias" className="field-label">
                    {intl.formatMessage({ id: "i09011" })}
                </label>
                <TextInputGroup id="webchat-setting-alias"
                                value={theme.alias ?? ""}
                                placeholder={intl.formatMessage({ id: "i09014" })}
                                invalid={!aliasValidation.isValid}
                                invalidText={aliasValidation.errorText}
                                onChange={value => onChange({ ...theme, alias: value })}
                />
            </div>

            <div className="field-label">
                {intl.formatMessage({ id: "i09012" })}
            </div>
            <div role="group"
                 aria-label={intl.formatMessage({ id: "i09012" })}
                 className="h-input-default flex items-center gap-4"
            >
                {theme.faviconUrl ? (<>
                    <ClickableImg src={theme.faviconUrl}
                                  alt={intl.formatMessage({ id: "i09012" })}
                                  height={24}
                                  width={24}
                    />
                    <button aria-label={intl.formatMessage({ id: "b0006" })}
                            onClick={() => onRemoveFile("faviconUrl")}
                    >
                        <DeleteIcon className="text-point-red size-4" />
                    </button>
                </>) : (
                    <ImageUploadBtn onUpload={(file, url) => onUploadFile("faviconUrl", file, url)} />
                )}
            </div>

            <label htmlFor="webchat-setting-demo-background-image" className="field-label">
                {intl.formatMessage({ id: "i09013" })}
            </label>
            <div role="group"
                 id="webchat-setting-demo-background-image"
                 aria-label={intl.formatMessage({ id: "i09013" })}
                 className="h-input-default flex items-center gap-4">
                {theme.demoBackgroundImageUrl ? (<>
                    <ClickableImg src={theme.demoBackgroundImageUrl}
                                  alt={intl.formatMessage({ id: "i09013" })}
                                  height={24}
                                  width={24}
                    />
                    <button aria-label={intl.formatMessage({ id: "b0006" })}
                            onClick={() => onRemoveFile("demoBackgroundImageUrl")}
                    >
                        <DeleteIcon className="text-point-red size-4" />
                    </button>
                </>) : (
                    <ImageUploadBtn onUpload={(file, url) => onUploadFile("demoBackgroundImageUrl", file, url)} />
                )}
            </div>
        </fieldset>
    );
};

export default WebchatBaseInputGroup;
